import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";

function PhotoSlot({ url, label, kind, uploading, onUpload, onDelete, testid }) { const imageUrl = (u) => u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  const { t } = useLang();
  return (
    <div className="sf-card p-4" data-testid={testid}>
      <div className="font-bold text-slate-900 text-sm mb-3">{label}</div>
      {url ? (
        <img src={imageUrl(url)} alt={label} className="w-full h-40 object-cover rounded-lg border border-slate-200" data-testid={`${testid}-img`} />
      ) : (
        <div className="w-full h-40 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-sm font-semibold text-slate-400">
          {t("photoNotAvailable")}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <label className="sf-btn-outline !py-2 !px-3 !text-xs cursor-pointer flex-1 text-center">
          <ImagePlus className="h-4 w-4 inline mr-1" /> {uploading === kind ? t("uploading") : t("uploadPhoto")}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], kind)} />
        </label>
        {url && (
          <button data-testid={`${testid}-delete`} className="inline-flex items-center gap-1 text-xs font-bold px-3 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors" onClick={() => onDelete(kind)}>
            <Trash2 className="h-3 w-3" /> {t("deletePhoto")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function BizPhotoManager({ biz, onChange, allowLogo }) {
  const { t } = useLang();
  const [uploading, setUploading] = useState(null);

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/master/photos?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
      toast.success(t("savedOk"));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setUploading(null);
    }
  };

  const delPhoto = async (kind, index) => {
    if (!window.confirm(t("confirmDeletePhoto"))) return;
    try {
      const { data } = await api.delete(`/master/photos/${kind}/${index}`);
      onChange(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="space-y-4" data-testid={allowLogo ? "biz-photos-master" : "biz-photos-admin"}>
      <div className="grid sm:grid-cols-2 gap-4">
        {allowLogo && <PhotoSlot url={biz.logo_url} label={t("logoPhoto")} kind="logo" uploading={uploading} onUpload={upload} onDelete={delPhoto} testid="biz-logo-photo" />}
        <PhotoSlot url={biz.owner_photo_url} label={t("ownerPhoto")} kind="owner" uploading={uploading} onUpload={upload} onDelete={delPhoto} testid={allowLogo ? "biz-owner-photo" : "admin-owner-photo"} />
      </div>
      <div>
        <div className="font-bold text-slate-900 text-sm mb-3">{t("shopPhotos")}</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {(biz.shop_photos || []).map((u, i) => (
            <div key={u} className="relative" data-testid={`biz-shop-photo-${i}`}>
              <img src={u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} alt={`Shop ${i + 1}`} className="w-full h-32 object-cover rounded-lg border border-slate-200" />
              <button
                data-testid={`biz-shop-photo-delete-${i}`}
                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 border border-slate-300 flex items-center justify-center text-rose-600 hover:bg-rose-50 transition-colors"
                onClick={() => delPhoto("shop", i)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <label className="h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-1 text-xs font-bold text-slate-500 cursor-pointer hover:border-emerald-500 hover:text-emerald-700 transition-colors" data-testid={allowLogo ? "biz-shop-photo-upload" : "admin-shop-photo-upload"}>
            <ImagePlus className="h-5 w-5" />
            {uploading === "shop" ? t("uploading") : t("uploadPhoto")}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0], "shop")} />
          </label>
        </div>
      </div>
    </div>
  );
}
